import React, { FC, useContext, useRef, useState } from 'react'
import { Autocomplete, createFilterOptions, FilterOptionsState, TextField } from '@mui/material'
import { Unit } from '../types'
import { AppContext } from '../contexts/AppProvider'
import useIsMobile from '../hooks/useIsMobile'
import UnitSearchMobileOverlay from './UnitSearchMobileOverlay'
import { createAutocompleteRenderOption } from './helpers/createAutocompleteRenderOption'
import { createUnitLabel } from '../utils/measureHelpers'

interface UnitAutocompleteProps {
  label: string
  autocompleteId: string
  unit: Unit
  onUnitChange: (newUnit: string) => void
  onOpenNewUnitRequestDialog: () => void
}

const filter = createFilterOptions<Unit>()


const UnitAutocomplete: FC<UnitAutocompleteProps> = ({
  label,
  autocompleteId,
  unit,
  onUnitChange,
  onOpenNewUnitRequestDialog
}) => {
  const isMobile = useIsMobile()
  const { appState } = useContext(AppContext)
  const { currentMeasure } = appState
  const unitsOfThisMeasure: { [unitName: string]: Unit } = currentMeasure.units
  const [isDesktopOpen, setIsDesktopOpen] = useState<boolean>(false)
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState<boolean>(false)
  const autoCompleteTextFieldRef = useRef<HTMLInputElement>(null)

  const handleUnitAutocompleteOpen = (event: React.SyntheticEvent): void => {
    if (isMobile) {
      event.preventDefault()
      event.stopPropagation()
      setIsMobileSearchOpen(true)
    } else {
      setIsDesktopOpen(true)
    }
  }

  const handleUnitAutocompleteClose = (): void => {
    setIsDesktopOpen(false)
  }

  const filterOptions = (options: Unit[], params: FilterOptionsState<Unit>): Unit[] => {
    const filtered = filter(options, params)

    const isSubstring = Object.values(unitsOfThisMeasure).some(option =>
      createUnitLabel(option).toLowerCase().includes(params.inputValue.toLowerCase())
    )

    if (params.inputValue !== '' && !isSubstring) {
      const requestUnit: Unit = {
        displayName: `Request unit "${params.inputValue}"`,
        system: '',
        symbol: '',
        codeName: '',
        url: '',
        toBaseUnit: (value) => value,
        fromBaseUnit: (value) => value
      }

      filtered.push(requestUnit)
    }

    return filtered
  }

  const autocompleteRenderOption = createAutocompleteRenderOption()

  const isOptionEqualToValue = (option: Unit, value: Unit): boolean => {
    return option.displayName === value.displayName
  }

  const handleUnitAutocompleteOnChange = (_event: React.ChangeEvent, newSelectedUnit: Unit): void => {
    if (newSelectedUnit?.displayName.startsWith('Request ')) {
      onOpenNewUnitRequestDialog()
    } else if (newSelectedUnit?.displayName !== null) {
      onUnitChange(newSelectedUnit.displayName)
    }
  }

  const autocompleteOptions: Unit[] = Object.values(unitsOfThisMeasure)

  return (
    <>
      <Autocomplete
        id={autocompleteId}
        options={autocompleteOptions}
        value={unit}
        groupBy={option => option.system}
        getOptionLabel={createUnitLabel}
        isOptionEqualToValue={isOptionEqualToValue}
        onChange={handleUnitAutocompleteOnChange}
        filterOptions={filterOptions}
        renderOption={autocompleteRenderOption}
        renderInput={(params) => (
          <TextField {...params} label={label} inputRef={autoCompleteTextFieldRef} />
        )}
        disableClearable
        blurOnSelect
        selectOnFocus
        autoHighlight
        openOnFocus={!isMobile} // very important for mobile!!! otherwise the mobile overlay will be stuck open in insane race condition edge case sometimes.

        open={isDesktopOpen}
        onOpen={handleUnitAutocompleteOpen}
        onClose={handleUnitAutocompleteClose}
      />
      <UnitSearchMobileOverlay
        open={isMobileSearchOpen}
        setOpen={setIsMobileSearchOpen}
        onOpenNewUnitRequestDialog={onOpenNewUnitRequestDialog}
        placeholderLabel={`Search ${label}`}
        unit={unit}
        unitAutocompleteRef={autoCompleteTextFieldRef}
      />
    </>
  )
}


export default UnitAutocomplete
